import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { formatCompactNumber, formatNumber } from "../utils";
import {
  useRequestsOverTime,
  useTokensOverTime,
  type OverTimeCategory,
  type OverTimeInterval,
  type StatsFilters,
} from "../api";

type Metric = "tokens" | "requests";
type ChartCategory = Exclude<OverTimeCategory, "deployments">;
type SeriesKey =
  | "prompt_tokens"
  | "completion_tokens"
  | "total_tokens"
  | "successful"
  | "failed"
  | "total";

type Props = { filters: StatsFilters };

const CATEGORY_OPTIONS: { id: ChartCategory; label: string }[] = [
  { id: "apis", label: "api" },
  { id: "models", label: "model" },
  { id: "flows", label: "flowRuntime" },
  { id: "rags", label: "ragQueries" },
];

const TOKEN_SERIES: { key: SeriesKey; label: string; color: string }[] = [
  { key: "prompt_tokens", label: "promptTokens", color: "#8b5cf6" },
  { key: "completion_tokens", label: "completionTokens", color: "#f87171" },
  { key: "total_tokens", label: "totalTokensSeries", color: "#14b8a6" },
];

const REQUEST_SERIES: { key: SeriesKey; label: string; color: string }[] = [
  { key: "successful", label: "successful", color: "#14b8a6" },
  { key: "failed", label: "failed", color: "#f87171" },
  { key: "total", label: "totalRequestsSeries", color: "#8b5cf6" },
];

const GRADIENTS: Record<SeriesKey, string> = {
  prompt_tokens: "colorPrompt",
  completion_tokens: "colorCompletion",
  total_tokens: "colorTotal",
  successful: "colorSuccessful",
  failed: "colorFailed",
  total: "colorRequestsTotal",
};

export function TotalsOverTime({ filters }: Props) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<ChartCategory>("models");
  const [metric, setMetric] = useState<Metric>("tokens");
  const interval: OverTimeInterval =
    filters.endDate.getTime() - filters.startDate.getTime() <=
    48 * 60 * 60 * 1000
      ? "hour"
      : "day";
  const tokens = useTokensOverTime(
    filters,
    category,
    interval,
    metric === "tokens",
  );
  const requests = useRequestsOverTime(
    filters,
    category,
    interval,
    metric === "requests",
  );

  const activeQuery = metric === "tokens" ? tokens : requests;
  const loading = activeQuery.isLoading;
  const data = activeQuery.data?.data ?? [];
  const unavailable =
    metric === "tokens" && Boolean(tokens.data?.unavailable_metrics?.length);
  const series = metric === "tokens" ? TOKEN_SERIES : REQUEST_SERIES;

  const formatBucket = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    const options: Intl.DateTimeFormatOptions =
      interval === "hour"
        ? {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "UTC",
          }
        : {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            timeZone: "UTC",
          };
    return new Intl.DateTimeFormat(undefined, options).format(parsed);
  };

  return (
    <SectionCard>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            {t("usageAndCost.totalsOverTime.title")}
          </h3>
          <div
            role="tablist"
            aria-label={t("usageAndCost.totalsOverTime.categoryAriaLabel")}
            className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-xl bg-secondary p-1"
          >
            {CATEGORY_OPTIONS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={category === id}
                onClick={() => setCategory(id)}
                className={cn(
                  "shrink-0 rounded-lg px-2 py-0.5 text-xs font-medium transition-colors",
                  category === id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground ",
                )}
              >
                {t(`usageAndCost.totalsOverTime.${label}`)}
              </button>
            ))}
          </div>
        </div>
        <div
          role="tablist"
          aria-label={t("usageAndCost.totalsOverTime.metricAriaLabel")}
          className="inline-flex shrink-0 items-center gap-0.5 rounded-xl bg-secondary p-1"
        >
          {(["tokens", "requests"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={metric === value}
              onClick={() => setMetric(value)}
              className={cn(
                "rounded-xl px-2.5 py-1 text-xs font-medium transition-colors",
                metric === value
                  ? "bg-background text-foreground shadow-sm rounded-lg"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`usageAndCost.totalsOverTime.${value}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-[280px] w-full flex-1" dir="ltr">
        {loading ? (
          <Skeleton className="h-full min-h-[280px] w-full" />
        ) : unavailable || data.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
            {unavailable
              ? t("usageAndCost.totalsOverTime.unavailable")
              : t("usageAndCost.totalsOverTime.noData")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 5, right: 16, bottom: 0, left: 0 }}
            >
              <defs>
                {series.map(({ key, color }) => (
                  <linearGradient
                    key={key}
                    id={GRADIENTS[key]}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                opacity={0.4}
                vertical={false}
              />
              <XAxis
                dataKey={(point) => point.bucket_start ?? point.date}
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                tickFormatter={(value: string) => formatBucket(value)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatCompactNumber(value as number)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--popover-foreground)",
                }}
                labelFormatter={(value) => formatBucket(String(value))}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend
                iconType="plainline"
                iconSize={18}
                verticalAlign="top"
                align="center"
                height={28}
                wrapperStyle={{
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                  paddingBottom: 8,
                }}
              />
              {series.map(({ key, label, color }) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={t(`usageAndCost.totalsOverTime.${label}`)}
                  stroke={color}
                  fill={`url(#${GRADIENTS[key]})`}
                  strokeWidth={2}
                  dot={{ r: 3, fill: color, strokeWidth: 0 }}
                  activeDot={{ r: 4 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </SectionCard>
  );
}
